import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Skeleton,
  Text,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { AddFieldSelection } from "../../../shared/components/AddFieldSelection";
import { WorkflowDataField } from "../../../shared/schemas/workflows";
import { useApi } from "../../../shared/utils/use-api";
import { zForm } from "../tag.const";
import { getNamedWorkflowDataField } from "../tag.utils";

type Props = {
  mode: "create" | "edit";
  form: UseFormReturn<z.infer<typeof zForm>>;
};

export default function TagFormControls(props: Props) {
  const { form } = props;

  return (
    <Flex direction="column" gap={6}>
      <FormControl isInvalid={form.formState.errors.name !== undefined}>
        <FormLabel>Name</FormLabel>
        <Input data-testid="tag-name-input" placeholder="Tag Name" {...form.register("name")} />
        {form.formState.errors.name === undefined && (
          <FormHelperText>Enter a readable name for the tag</FormHelperText>
        )}
        <FormErrorMessage>{form.formState.errors.name?.message}</FormErrorMessage>
      </FormControl>

      <FormControl isInvalid={form.formState.errors.id !== undefined}>
        <FormLabel>Tag ID</FormLabel>
        <Input
          data-testid="tag-id-input"
          disabled={props.mode === "edit"}
          fontFamily={"mono"}
          placeholder="tag-name"
          {...form.register("id", {
            onChange: (e) =>
              form.setValue("id", e.target.value.toLowerCase().replace(/[^a-z0-9-{}]/g, "-")),
          })}
        />

        {props.mode === "create" && form.formState.errors.id === undefined && (
          <FormHelperText>
            For example:{" "}
            <Text as="span" color="blue.600" fontFamily="mono">{`intake-patient:{patient}`}</Text>
          </FormHelperText>
        )}
        <FormErrorMessage>{form.formState.errors.id?.message}</FormErrorMessage>
      </FormControl>

      <Flex direction="column" gap={2}>
        {form.watch("input").map(([name, field], i) => (
          <Box key={name}>
            <HStack justify="space-between">
              <HStack>
                <Text color="gray.400" fontWeight="normal">
                  input:
                </Text>
                <Text fontWeight="medium">{name}</Text>
              </HStack>
              <TagFormField
                fieldTypeName={
                  (field as WorkflowDataField | null) === null
                    ? "Choose type"
                    : getNamedWorkflowDataField(field as WorkflowDataField)
                }
                hasError={form.formState.errors.input?.[i] !== undefined}
                isEditMode={props.mode === "edit"}
                name={name}
                onAddField={(field) => form.setValue(`input.${i}`, [name, field])}
              />
            </HStack>
          </Box>
        ))}
      </Flex>
    </Flex>
  );
}

function TagFormField(props: {
  name: string;
  fieldTypeName: string;
  hasError: boolean;
  isEditMode: boolean;
  onAddField: (field: WorkflowDataField) => void;
}) {
  const { queries } = useApi();
  const entityOptionsQuery = useQuery(queries.entityOptions());

  switch (entityOptionsQuery.status) {
    case "loading":
      return <Skeleton height="20px" />;

    case "error":
      return <FormHelperText>Failed to load entity options</FormHelperText>;

    case "success":
      return (
        <AddFieldSelection
          data-testid="add-field"
          entityOptions={entityOptionsQuery.data.data}
          onAdd={props.onAddField}
        >
          <Button
            colorScheme={props.hasError ? "red" : "gray"}
            data-input-key={props.name}
            isDisabled={props.isEditMode}
            maxW="64"
            size="sm"
            variant="outline"
          >
            <Text isTruncated>{props.fieldTypeName}</Text>
          </Button>
        </AddFieldSelection>
      );
  }
}
